import { MenuItem } from "@mui/material";
import { type FC } from "react";
import { NavLink, useLocation } from "react-router";

import { BlurContainer } from "~/components/UI/BlurContainer";
import { MoreButton } from "~/components/UI/MoreButton";
import { ROUTES } from "~/constants/routing";
import { getInsightCategoryColor } from "~/feature/insights/constants";
import { type IInsightCategory } from "~/feature/insights/types";
import { splitInsightCategoriesForNav } from "~/feature/insights/utils/splitInsightCategoriesForNav";

import * as Styled from "./InsightsNav.styles";

interface IInsightsNavProps {
  categories: IInsightCategory[];
}

export const InsightsNav: FC<IInsightsNavProps> = ({ categories }) => {
  const { pathname } = useLocation();

  const getCategorySlug = () => {
    const match = pathname.match(/^\/insights\/category\/(.+)$/);
    return match ? match[1] : null;
  };

  const activeCategorySlug = getCategorySlug();
  const isAll =
    !activeCategorySlug &&
    !pathname.startsWith("/insights/tag/") &&
    !pathname.startsWith("/insights/type/");

  const { visibleCategories, overflowCategories } =
    splitInsightCategoriesForNav(categories);

  const isOverflowActive = overflowCategories.some(
    (category) => category.slug === activeCategorySlug
  );

  const renderCategoryPill = (category: IInsightCategory) => {
    const isActive = activeCategorySlug === category.slug;
    const color = getInsightCategoryColor(category.color);

    return (
      <Styled.PillWrapper key={category.slug}>
        <Styled.PillButton
          component={NavLink}
          to={ROUTES.insightsCategory.createURL(category.slug)}
          $isActive={isActive}
          $color={isActive ? color : undefined}
        >
          <Styled.PillText
            variant="caption"
            $isActive={isActive}
            $color={isActive ? color : undefined}
          >
            {category.name}
          </Styled.PillText>
        </Styled.PillButton>
      </Styled.PillWrapper>
    );
  };

  return (
    <Styled.Root>
      <Styled.NavScroller>
        <BlurContainer>
          <Styled.Container>
            <Styled.PillWrapper>
              <Styled.PillButton
                component={NavLink}
                to={ROUTES.insights.createURL()}
                end
                $isActive={isAll}
              >
                <Styled.PillText variant="caption" $isActive={isAll}>
                  All
                </Styled.PillText>
              </Styled.PillButton>
            </Styled.PillWrapper>

            {visibleCategories.map(renderCategoryPill)}

            {overflowCategories.length > 0 && (
              <Styled.PillWrapper>
                <MoreButton text="More" isActive={isOverflowActive}>
                  {overflowCategories.map((category) => {
                    const isActive = activeCategorySlug === category.slug;
                    const color = getInsightCategoryColor(category.color);

                    return (
                      <MenuItem
                        key={category.slug}
                        component={NavLink}
                        to={ROUTES.insightsCategory.createURL(category.slug)}
                        selected={isActive}
                      >
                        <Styled.PillText
                          variant="caption"
                          $isActive={isActive}
                          $color={isActive ? color : undefined}
                        >
                          {category.name}
                        </Styled.PillText>
                      </MenuItem>
                    );
                  })}
                </MoreButton>
              </Styled.PillWrapper>
            )}
          </Styled.Container>
        </BlurContainer>
      </Styled.NavScroller>
    </Styled.Root>
  );
};
